import { ComponentPropsWithoutRef } from 'react';

import classNames from 'classnames';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import { Button, ExternalLink } from '@uikit';

import TeamMemberList from '@launchpad/components/common/TeamMemberList/TeamMemberList';

import { ProjectContentTableOfContentTab } from '../types';
import { ItemsProps } from '../types';
import {
  gallery,
  investors,
  keyFeatures,
  partners,
  revenues,
  teamMembers,
  tokenUtility,
  tractions,
} from './constants';

export interface ListProps extends ComponentPropsWithoutRef<'ul'> {
  items: Array<ItemsProps>;
  description?: string;
  descriptionClassName?: string;
}

const List = ({ items, description, className, descriptionClassName }: ListProps) => {
  return (
    <>
      {Boolean(description) && (
        <p
          className={classNames(
            'mb-4 sm:text-2xl font-semibold text-neutral-500',
            descriptionClassName,
          )}
        >
          {description}
        </p>
      )}
      <ul className={classNames('list-disc pl-6', className)}>
        {items.map(({ id, content, item }) => {
          if (Array.isArray(item)) {
            return (
              <li key={id}>
                {content}
                <ul className="list-disc pl-6">
                  {item.map(({ id: subId, subItem }) => (
                    <li key={subId}>{subItem}</li>
                  ))}
                </ul>
              </li>
            );
          } else {
            return (
              <li className="max-sm:text-sm" key={id}>
                {item}
              </li>
            );
          }
        })}
      </ul>
    </>
  );
};

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'intro',
    title: '28-Day Refund Policy',
    content: (
      <p className="mb-6 font-normal">
        Reform DAO's IDO introduces a unique "28-Day Refund Policy." After the TGE, any participant
        in the IDO can request a refund of their investment if they have not claimed any tokens.
        Once a user claims even a single token or 28 days have passed since TGE, they are no longer
        eligible for a refund. Upon requesting a refund, the user's allocation is forfeited, the
        claim button is disabled, and a manual refund process is initiated, which can take up to 72
        hours.
      </p>
    ),
  },
  {
    id: 'introduction',
    title: 'Introduction',
    content: (
      <>
        <p className="mb-4 font-normal">
          Reform DAO is redefining the landscape of liquidity provision and market-making through
          AI-powered algorithms and community governance. It leverages protocol-owned liquidity to
          align incentives among all stakeholders, including protocols, communities, and market
          makers. Reform DAO ensures fairness and transparency in liquidity markets, replacing
          traditional market-making models with a decentralized and transparent ecosystem that
          benefits all participants.
        </p>
        <p className="mb-4 font-normal">
          By deploying sophisticated machine learning models, Reform DAO delivers predictive
          insights, optimizing market-making strategies across exchanges. The platform is powered by
          advanced regression and correlation analysis techniques to predict cryptocurrency price
          trends and manage liquidity efficiently. These AI-driven models can execute up to 3
          million trades per day, making Reform DAO a leading force in decentralized finance (DeFi)
          and centralized exchanges (CEXs).
        </p>
        <p className="mb-4 font-normal">
          Since its inception, Reform DAO has achieved over $3.5 billion in trading volume within
          just four months, generating $150,000 in revenue before even launching its native token.
          The DAO's live trading dashboard, paired with read-only APIs, provides full transparency,
          allowing participants to monitor every trade and the Realized Profits that are funneled
          back into the ecosystem for buybacks, operations, and R&D.
        </p>
      </>
    ),
  },
  {
    id: 'key-features',
    title: 'Key Features',
    content: (
      <>
        <List items={keyFeatures} />
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink href="https://docs.reformdao.com/">Learn More</ExternalLink>
        </Button>
        <div className="mt-10 overflow-hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{
              enabled: true,
              clickable: true,
            }}
            slidesPerView="auto"
            className="rounded-lg -mx-1 pb-8"
          >
            {gallery.map((galleryItem, index) => (
              <SwiperSlide key={index} className={classNames('w-4/5 px-1')}>
                <ImageProxy
                  alt={galleryItem.alt}
                  width={1920}
                  height={1080}
                  quality={80}
                  className="w-full h-auto rounded-lg select-none"
                  src={galleryItem.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </>
    ),
  },
  {
    id: 'traction',
    title: 'Traction',
    content: (
      <>
        <List items={tractions} />
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink href="https://www.reformdao.com/#milestone">Learn More</ExternalLink>
        </Button>
      </>
    ),
  },
  {
    id: 'investors',
    title: 'Investors',
    content: (
      <>
        <ul className="grid grid-cols-1 sm:grid-cols-4 gap-1.5">
          {investors.map(({ name, image, width, className, height }) => {
            return (
              <div key={name} className="flex flex-col items-center gap-2">
                <li className={classNames('rounded-full', className)}>
                  <ImageProxy
                    alt={name}
                    width={width}
                    height={height}
                    quality={80}
                    className="w-full h-full object-contain rounded-full"
                    src={image}
                  />
                </li>
                <span className="font-semibold text-center">{name}</span>
              </div>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'partners',
    title: 'Partners',
    content: (
      <div>
        <ul className="grid grid-cols-1 sm:grid-cols-4 gap-1.5 mb-1.5">
          {partners.map(({ name, image, width, height, className }) => {
            return (
              <div key={name} className="flex flex-col items-center gap-2">
                <li className={classNames('rounded-full', className)}>
                  <ImageProxy
                    alt={name}
                    width={width}
                    height={height}
                    quality={80}
                    className="w-full h-full object-contain rounded-full"
                    src={image}
                  />
                </li>
                <span className="font-semibold">{name}</span>
              </div>
            );
          })}
        </ul>
      </div>
    ),
  },
  {
    id: 'revenue',
    title: 'Revenue',
    content: <List items={revenues} />,
  },
  {
    id: 'token-utility',
    title: 'Token Utility',
    content: <List items={tokenUtility} />,
  },
  {
    id: 'tokenomics',
    title: 'Token Metrics',
    content: (
      <>
        <ImageProxy
          alt="Token Metrics: 39% Bonding Treasury, 30% Staking Treasury, 13% Team & Advisors, 10% Operations Treasury, 3% Early Backers, 2.8% Market Making, 2% Launch Incentives, 0.2% Liquidity Pool"
          src="/launchpad/stories/reform-dao/gallery/tokenomics.png"
          className="w-full h-auto rounded-lg mb-4 object-contain shadow-main block"
          quality={80}
          width={774}
          height={325}
        />
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink href="https://docs.reformdao.com/tokenomics/usdrfrm">
            Learn More
          </ExternalLink>
        </Button>
      </>
    ),
  },
  {
    id: 'team',
    title: 'Team',
    content: (
      <>
        <TeamMemberList
          className="!grid-cols-1 max-md:flex-col"
          cardClassName="max-md:flex-col"
          teamMembers={teamMembers}
        />
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink href="https://docs.reformdao.com/overview/the-team">
            Learn More
          </ExternalLink>
        </Button>
      </>
    ),
  },
  {
    id: 'roadmap',
    title: 'Roadmap',
    content: (
      <>
        <ImageProxy
          alt="Roadmap"
          src="/launchpad/stories/reform-dao/gallery/roadmap.png"
          className="w-full h-auto rounded-lg mb-4 object-contain shadow-main block"
          quality={80}
          width={774}
          height={469}
        />
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink href="https://docs.reformdao.com/overview/roadmap">Learn More</ExternalLink>
        </Button>
      </>
    ),
  },
];
